/* ============================================================================
   The scoreboard chart: four equity curves on one axis.

   Deliberate choices worth keeping if you refactor this:
   - P&L against seed capital, not raw equity, so all four start at zero and the
     comparison is honest. ROI is a toggle, never a second y-axis.
   - Colour identity is fixed per agent and is never re-assigned by rank.
     Dash pattern encodes the vendor, so identity survives without colour.
   - Every visible series is direct-labelled at its live end, so the legend is a
     convenience rather than the only key.
   - Canvas for the marks, DOM for the labels. The curve redraws at feed rate
     without React doing any per-tick reconciliation.
   ========================================================================== */

import { useEffect, useMemo, useRef, useState } from 'react'
import { AGENTS, RULES } from '../config/competition'
import { clock, compactMoney, signedCompactMoney, signedPct } from '../core/format'
import type { CompetitionState, EquitySample } from '../core/types'
import { Panel } from './primitives/Panel'

type Timeframe = '15M' | '1H' | '6H' | 'ALL'
type Mode = 'pnl' | 'roi'

const TF_MS: Record<Timeframe, number> = {
  '15M': 15 * 60_000,
  '1H': 3600_000,
  '6H': 6 * 3600_000,
  ALL: Infinity,
}

const PAD = { top: 14, right: 116, bottom: 22, left: 52 }

function niceStep(range: number, target: number): number {
  const raw = range / Math.max(1, target)
  const mag = 10 ** Math.floor(Math.log10(Math.abs(raw) || 1))
  const norm = raw / mag
  const step = norm > 5 ? 10 : norm > 2 ? 5 : norm > 1 ? 2 : 1
  return step * mag
}

/** CSS custom properties are resolved once per paint so canvas matches the DOM. */
function readTokens(el: HTMLElement) {
  const cs = getComputedStyle(el)
  const v = (n: string) => cs.getPropertyValue(n).trim()
  return {
    ink: v('--ink'),
    ink2: v('--ink-2'),
    ink3: v('--ink-3'),
    line: v('--line'),
    lineStrong: v('--line-strong'),
    up: v('--up'),
    down: v('--down'),
    series: AGENTS.map((a) => v(a.color.replace('var(', '').replace(')', ''))),
  }
}

export function PnlChart({ state }: { state: CompetitionState }) {
  const [tf, setTf] = useState<Timeframe>('1H')
  const [mode, setMode] = useState<Mode>('pnl')
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [table, setTable] = useState(false)
  const [hover, setHover] = useState<{ x: number; i: number } | null>(null)

  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect
      setSize({ w: Math.floor(r.width), h: Math.floor(r.height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /** Window the curve to the selected timeframe and to one sample per pixel. */
  const samples = useMemo<EquitySample[]>(() => {
    const all = state.equity
    if (!all.length) return []
    const now = all[all.length - 1].t
    const from = now - TF_MS[tf]
    const win = TF_MS[tf] === Infinity ? all : all.filter((s) => s.t >= from)
    const budget = Math.max(80, size.w - PAD.left - PAD.right)
    if (win.length <= budget) return win
    const stride = Math.ceil(win.length / budget)
    const out: EquitySample[] = []
    for (let i = 0; i < win.length; i += stride) out.push(win[i])
    if (out[out.length - 1] !== win[win.length - 1]) out.push(win[win.length - 1])
    return out
  }, [state.equity, state.version, tf, size.w])

  /* The curve samples every few seconds, but the cards mark to market every
     tick. Appending the live equity keeps the tip, the legend and the roster
     showing the same number instead of three near-misses. */
  const series = useMemo<EquitySample[]>(() => {
    const last = samples[samples.length - 1]
    if (!last || !Object.keys(state.portfolios).length) return samples
    const v: Record<string, number> = { ...last.v }
    for (const a of AGENTS) {
      const eq = state.portfolios[a.id]?.equity
      if (eq !== undefined) v[a.id] = eq
    }
    return [...samples, { t: Math.max(last.t + 1, Date.now()), v }]
  }, [samples, state.version])

  const valueOf = (s: EquitySample, id: string): number => {
    const eq = s.v[id] ?? RULES.seedCapital
    return mode === 'pnl' ? eq - RULES.seedCapital : (eq / RULES.seedCapital - 1) * 100
  }

  const visible = AGENTS.filter((a) => !hidden.has(a.id))

  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap || !size.w || !size.h || series.length < 2) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = size.w * dpr
    canvas.height = size.h * dpr
    canvas.style.width = `${size.w}px`
    canvas.style.height = `${size.h}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size.w, size.h)

    const T = readTokens(wrap)
    const plotW = size.w - PAD.left - PAD.right
    const plotH = size.h - PAD.top - PAD.bottom

    let lo = 0
    let hi = 0
    for (const s of series) {
      for (const a of visible) {
        const v = valueOf(s, a.id)
        if (v < lo) lo = v
        if (v > hi) hi = v
      }
    }
    const span = hi - lo || (mode === 'pnl' ? 1000 : 1)
    lo -= span * 0.12
    hi += span * 0.12

    const t0 = series[0].t
    const t1 = series[series.length - 1].t
    const X = (t: number) => PAD.left + ((t - t0) / Math.max(1, t1 - t0)) * plotW
    const Y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo)) * plotH

    ctx.font = '10px "JetBrains Mono", monospace'
    ctx.textBaseline = 'middle'

    // grid + value axis
    const step = niceStep(hi - lo, 5)
    ctx.lineWidth = 1
    for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
      const y = Math.round(Y(v)) + 0.5
      const zero = Math.abs(v) < step * 0.01
      ctx.strokeStyle = zero ? T.lineStrong : T.line
      ctx.beginPath()
      ctx.moveTo(PAD.left, y)
      ctx.lineTo(PAD.left + plotW, y)
      ctx.stroke()
      ctx.fillStyle = zero ? T.ink2 : T.ink3
      ctx.textAlign = 'right'
      // The baseline is exactly zero, never a rounded "-$0.00".
      const label = zero
        ? mode === 'pnl'
          ? '$0'
          : '0%'
        : mode === 'pnl'
          ? compactMoney(v)
          : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
      ctx.fillText(label, PAD.left - 8, y)
    }

    // time axis. A young round spans seconds, so the label resolution follows
    // the window rather than always printing the hour.
    ctx.textAlign = 'center'
    ctx.fillStyle = T.ink3
    const stamp = t1 - t0 < 20 * 60_000 ? (t: number) => clock(t).slice(3) : (t: number) => clock(t).slice(0, 5)
    const ticks = Math.max(2, Math.min(7, Math.floor(plotW / 110)))
    for (let i = 0; i <= ticks; i++) {
      const t = t0 + ((t1 - t0) * i) / ticks
      const x = X(t)
      ctx.fillText(stamp(t), Math.min(size.w - PAD.right + 10, Math.max(PAD.left, x)), size.h - PAD.bottom / 2)
    }

    // series
    const ends: Array<{ id: string; name: string; color: string; y: number; v: number }> = []
    visible.forEach((a) => {
      const idx = AGENTS.findIndex((x) => x.id === a.id)
      const color = T.series[idx] || T.ink
      ctx.save()
      ctx.beginPath()
      series.forEach((s, i) => {
        const x = X(s.t)
        const y = Y(valueOf(s, a.id))
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.strokeStyle = color
      ctx.lineWidth = 2
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.setLineDash(a.dash)
      ctx.shadowColor = color
      ctx.shadowBlur = 4
      ctx.stroke()
      ctx.restore()

      const last = series[series.length - 1]
      const v = valueOf(last, a.id)
      const y = Y(v)
      ends.push({ id: a.id, name: a.name, color, y, v })

      ctx.beginPath()
      ctx.arc(X(last.t), y, 3, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
    })

    // direct labels, pushed apart so they never overlap
    ends.sort((a, b) => a.y - b.y)
    const MIN_GAP = 24
    for (let i = 1; i < ends.length; i++) {
      if (ends[i].y - ends[i - 1].y < MIN_GAP) ends[i].y = ends[i - 1].y + MIN_GAP
    }
    for (let i = ends.length - 1; i > 0; i--) {
      if (ends[i].y > size.h - PAD.bottom) ends[i].y = size.h - PAD.bottom
      if (ends[i].y - ends[i - 1].y < MIN_GAP) ends[i - 1].y = ends[i].y - MIN_GAP
    }
    ctx.textAlign = 'left'
    for (const e of ends) {
      const x = PAD.left + plotW + 10
      ctx.strokeStyle = e.color
      ctx.globalAlpha = 0.5
      ctx.lineWidth = 1
      ctx.setLineDash([])
      ctx.beginPath()
      ctx.moveTo(PAD.left + plotW + 2, Y(e.v))
      ctx.lineTo(x - 2, e.y)
      ctx.stroke()
      ctx.globalAlpha = 1
      ctx.fillStyle = e.color
      ctx.font = '600 10px "Martian Mono", monospace'
      ctx.fillText(e.name, x, e.y - 5)
      ctx.fillStyle = e.v > 0 ? T.up : e.v < 0 ? T.down : T.ink2
      ctx.font = '10px "JetBrains Mono", monospace'
      ctx.fillText(
        mode === 'pnl' ? signedCompactMoney(e.v) : signedPct(e.v, 1),
        x,
        e.y + 6,
      )
    }

    // crosshair
    if (hover && series[hover.i]) {
      const s = series[hover.i]
      const x = Math.round(X(s.t)) + 0.5
      ctx.strokeStyle = T.lineStrong
      ctx.setLineDash([3, 3])
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x, PAD.top)
      ctx.lineTo(x, PAD.top + plotH)
      ctx.stroke()
      ctx.setLineDash([])
      visible.forEach((a) => {
        const idx = AGENTS.findIndex((z) => z.id === a.id)
        ctx.beginPath()
        ctx.arc(x, Y(valueOf(s, a.id)), 3.5, 0, Math.PI * 2)
        ctx.fillStyle = T.series[idx] || T.ink
        ctx.fill()
        ctx.strokeStyle = '#0a0d12'
        ctx.lineWidth = 2
        ctx.stroke()
      })
    }
  }, [series, size, hidden, mode, hover, state.version])

  const onMove = (e: React.MouseEvent) => {
    if (!series.length || !size.w) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const plotW = size.w - PAD.left - PAD.right
    const ratio = (x - PAD.left) / Math.max(1, plotW)
    if (ratio < -0.02 || ratio > 1.02) return setHover(null)
    const i = Math.round(Math.max(0, Math.min(1, ratio)) * (series.length - 1))
    setHover({ x, i })
  }

  const toggle = (id: string) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (next.size < AGENTS.length - 1) next.add(id)
      return next
    })

  const hovered = hover ? series[hover.i] : null
  const latest = series[series.length - 1]

  return (
    <Panel
      title="EQUITY CURVE / FOUR AGENTS"
      grow
      className="chart"
      actions={
        <div className="seg-row">
          <Segmented
            options={['pnl', 'roi'] as Mode[]}
            value={mode}
            onChange={setMode}
            labels={{ pnl: 'P&L $', roi: 'ROI %' }}
          />
          <Segmented
            options={['15M', '1H', '6H', 'ALL'] as Timeframe[]}
            value={tf}
            onChange={setTf}
          />
          <button
            className={`seg__btn seg__btn--solo ${table ? 'is-on' : ''}`}
            onClick={() => setTable((t) => !t)}
            title="Read the same data as a table"
          >
            TABLE
          </button>
        </div>
      }
    >
      <div className="chart__legend">
        {AGENTS.map((a) => {
          const off = hidden.has(a.id)
          const v = latest ? valueOf(latest, a.id) : 0
          return (
            <button
              key={a.id}
              className={`legend ${off ? 'is-off' : ''}`}
              onClick={() => toggle(a.id)}
              title={off ? `Show ${a.name}` : `Hide ${a.name}`}
            >
              <span
                className="legend__key"
                style={{
                  background: off ? 'transparent' : a.color,
                  borderColor: a.color,
                  backgroundImage:
                    !off && a.dash.length
                      ? 'repeating-linear-gradient(90deg, transparent 0 2px, rgba(0,0,0,.6) 2px 4px)'
                      : undefined,
                }}
              />
              <span className="legend__name">{a.name}</span>
              <span className={`legend__val ${v > 0 ? 'u-up' : v < 0 ? 'u-down' : 'u-flat'}`}>
                {mode === 'pnl' ? signedCompactMoney(v) : signedPct(v, 1)}
              </span>
            </button>
          )
        })}
      </div>

      {table ? (
        <div className="chart__table u-scroll">
          <table>
            <caption className="u-sr">Equity curve values by agent</caption>
            <thead>
              <tr>
                <th scope="col">TIME</th>
                {AGENTS.map((a) => (
                  <th key={a.id} scope="col">
                    {a.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {series
                .slice(-40)
                .reverse()
                .map((s) => (
                  <tr key={s.t}>
                    <td>{clock(s.t)}</td>
                    {AGENTS.map((a) => {
                      const v = valueOf(s, a.id)
                      return (
                        <td key={a.id} className={v > 0 ? 'u-up' : v < 0 ? 'u-down' : 'u-flat'}>
                          {mode === 'pnl' ? signedCompactMoney(v) : signedPct(v, 1)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className="chart__canvas"
          ref={wrapRef}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <canvas ref={canvasRef} />
          {hovered && hover ? (
            <div
              className="chart__tip"
              style={{
                left: Math.min(Math.max(hover.x + 14, 8), Math.max(8, size.w - 210)),
              }}
            >
              <div className="chart__tip-time">{clock(hovered.t)}</div>
              {[...AGENTS]
                .filter((a) => !hidden.has(a.id))
                .map((a) => ({ a, v: valueOf(hovered, a.id) }))
                .sort((x, y) => y.v - x.v)
                .map(({ a, v }) => (
                  <div key={a.id} className="chart__tip-row">
                    <span className="chart__tip-key" style={{ background: a.color }} />
                    <span className="chart__tip-name">{a.name}</span>
                    <span className={v > 0 ? 'u-up' : v < 0 ? 'u-down' : 'u-flat'}>
                      {mode === 'pnl' ? signedCompactMoney(v) : signedPct(v, 1)}
                    </span>
                  </div>
                ))}
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: T[]
  value: T
  onChange: (v: T) => void
  labels?: Record<string, string>
}) {
  return (
    <div className="seg" role="group">
      {options.map((o) => (
        <button
          key={o}
          className={`seg__btn ${o === value ? 'is-on' : ''}`}
          onClick={() => onChange(o)}
          aria-pressed={o === value}
        >
          {labels?.[o] ?? o}
        </button>
      ))}
    </div>
  )
}
